import { Form, Formik, useField } from "formik";
import * as Yup from "yup";

import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";

import { CampaignAnalyticsService } from "@analytics/index";

import { AttachmentType, EmailType } from "@interfaces/campaign.type";

import CampaignContact from "@pages/CreateCampaign/components/CampaignContact";

import { PopupActions } from "@store/popupStore";
import { useUserStore } from "@store/userStore";

import theme from "@theme/index";

// Validation Schema
const testEmailSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Required"),
});

interface ICampaignReviewProperties {
  campaignEmails: EmailType[];
  sendTestEmail: (email: string) => Promise<void>;
  showAuthenticateEmail: () => void;
}

const ReviewCampaign = ({ campaignEmails, sendTestEmail, showAuthenticateEmail }: ICampaignReviewProperties) => {
  const user = useUserStore();

  const [attachmentsField] = useField<AttachmentType[]>("attachments");

  const totalAttachments = attachmentsField.value ? attachmentsField.value.length : 0;

  const handleSendTestEmail = () => {
    if (user.nylasAuthenticated) {
      PopupActions.openModal({
        content: (
          <Formik
            initialValues={{
              email: "",
            }}
            onSubmit={async (values) => {
              await sendTestEmail(values.email);
              PopupActions.closeModal();
              CampaignAnalyticsService.sendTestEmail();
            }}
            validationSchema={testEmailSchema}
          >
            {({ errors, handleBlur, handleChange, isSubmitting, touched, values }) => (
              <Form>
                <Typography gutterBottom variant="body1">
                  Preview your campaign by sending a test email to any address.
                </Typography>
                <TextField
                  error={touched.email && Boolean(errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label="Email Address"
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  variant="outlined"
                />
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  sx={{
                    my: 2,
                  }}
                  type="submit"
                  variant="contained"
                >
                  {isSubmitting ? <CircularProgress size={24} /> : "Send Test Email"}
                </Button>
              </Form>
            )}
          </Formik>
        ),
        title: "Send Test Email",
      });
    } else {
      showAuthenticateEmail();
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box>
        <Box
          sx={{
            alignItems: "flex-end",
            display: "flex",
            flexDirection: "row",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">Review Campaign</Typography>
          <Button
            color="info"
            onClick={handleSendTestEmail}
            size="small"
            startIcon={<SendRoundedIcon fontSize="small" />}
            variant="contained"
          >
            Send Test
          </Button>
        </Box>
        <Typography sx={{ mt: 1.5 }} variant="body1">
          <strong>{campaignEmails.length}</strong> emails
          {totalAttachments > 0 && (
            <>
              {" with "}
              <strong>{totalAttachments}</strong> {totalAttachments === 1 ? "attachment" : "attachments"}
            </>
          )}
          {" will be sent to the following contacts:"}
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.borderColor.dark}`,
          borderRadius: 1,
          flexGrow: 1,
          maxHeight: "calc(100vh - 407px)",
          maxWidth: "100%",
          overflowY: "auto",
          width: "480px",
        }}
      >
        {campaignEmails.map((email, index) => (
          <CampaignContact
            email={email}
            emailId={email.id}
            isBanded={index % 2 === 1}
            isNonInteractive
            isSelected={false}
            key={email.id}
            removeEmail={() => {
              /* Do Nothing */
            }}
            setSelectedEmail={() => {
              /* Do Nothing */
            }}
            showActions={false}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ReviewCampaign;
